import { useCallback, useEffect, useRef } from 'react';

type UseCallbackTimeoutReturn = {
    start: () => void;
    clear: () => void;
    reset: () => void;
};

/**
 * Hook that accepts a callback and provides triggers to run the callback after a given delay
 **/
const useCallbackTimeout = (callback: () => void, delay: number): UseCallbackTimeoutReturn => {
    const callbackRef = useRef(callback);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const clear = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // Update the callback reference if it changes
    useEffect(() => {
        callbackRef.current = callback;
        return clear;
    }, [callback, clear]);

    const start = useCallback(() => {
        clear();
        timeoutRef.current = setTimeout(() => {
            callbackRef.current();
        }, delay);
    }, [delay, clear]);

    const reset = useCallback(() => start(), [start]);

    return { start, clear, reset };
};

export { useCallbackTimeout };
